import firebase from '@/firebase'

// In-flight request cache so concurrent components don't kick off duplicate fetches.
const inflight = new Map()

function dedupe (key, start) {
  if (inflight.has(key)) {
    return inflight.get(key)
  }
  const promise = start().finally(() => {
    inflight.delete(key)
  })
  inflight.set(key, promise)
  return promise
}

function fetchJson (url) {
  return fetch(url).then(res => {
    if (!res.ok) {
      throw new Error('Failed to load report from Storage (status ' + res.status + ')')
    }
    return res.json()
  })
}

function fetchJsonFromStoragePath (path) {
  return dedupe(path, () => firebase.storage.ref().child(path).getDownloadURL().then(fetchJson))
}

// Storage rules can't authorize role-less users for modelReports/{clientId}/...: cross-service
// rules cap evaluation at 2 Firestore documents and the ownsClient check needs more, so a direct
// getDownloadURL() gets 403 for any user whose access comes only from model assignment. The
// getModelReportDownloadUrl callable makes the same access decision server-side (no limit) and
// returns a short-lived signed URL. Direct getDownloadURL stays as a fallback so role users keep
// working even if the callable is unavailable.
function fetchClientReport (clientId, path) {
  return dedupe(path, () =>
    firebase.functions.httpsCallable('getModelReportDownloadUrl')({ clientId: clientId })
      .then(result => fetchJson(result.data.url))
      .catch(err => {
        console.error('Signed URL fetch failed, falling back to direct Storage read:', err)
        return firebase.storage.ref().child(path).getDownloadURL().then(fetchJson)
      })
  )
}

function parseInline (value) {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (e) {
      console.error('Failed to parse inline report:', e)
      return null
    }
  }
  return value
}

export async function loadModelReportFromClient (client) {
  if (!client) return null
  // Share mode inlines the parsed report server-side (functions/shared/shared.js) while keeping
  // model_report_storage set — prefer the inline object so anonymous viewers never hit Storage.
  if (client.model_report && typeof client.model_report === 'object') {
    return client.model_report
  }
  if (client.model_report_storage) {
    if (client.id) {
      return fetchClientReport(client.id, client.model_report_storage)
    }
    return fetchJsonFromStoragePath(client.model_report_storage)
  }
  if (client.model_report) {
    return parseInline(client.model_report)
  }
  return null
}

export function hasModelReport (item) {
  return !!(item && (item.model_report_storage || item.model_report))
}

export async function loadAutoModelReport (autoDoc) {
  if (!autoDoc) return null
  if (autoDoc.report_storage) {
    return fetchJsonFromStoragePath(autoDoc.report_storage)
  }
  if (autoDoc.report) {
    return parseInline(autoDoc.report)
  }
  return null
}

export function hasAutoModelReport (item) {
  return !!(item && (item.report_storage || item.report))
}

export function getModelReportQuality (item) {
  if (!item) return null
  if (item.model_report_summary && item.model_report_summary.quality !== undefined) {
    return item.model_report_summary.quality
  }
  if (item.model_report) {
    const parsed = parseInline(item.model_report)
    return parsed && parsed.quality !== undefined ? parsed.quality : null
  }
  return null
}
